import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { COUNTRIES, flagUrl } from "@/lib/countries";
import { fmtUAE, TZ_LABEL } from "@/lib/dates";
import { toast } from "sonner";
import { Trash2, Plus, Save, Users, Calendar, Star, Settings, Sparkles, Info, Pencil } from "lucide-react";
import PlayerDetailModal from "@/components/PlayerDetailModal";

const BONUS_DEFS = [
  { key: "champion", label: "Champion", pts: 15, kind: "country" },
  { key: "runner_up", label: "Runner-up", pts: 8, kind: "country" },
  { key: "golden_boot", label: "Golden Boot", pts: 10, kind: "player" },
  { key: "dark_horse", label: "Dark Horse", pts: 8, kind: "country" },
  { key: "golden_glove", label: "Golden Glove", pts: 6, kind: "player" },
  { key: "final_goals_ou", label: "Final Goals (O/U 2.5)", pts: 5, kind: "ou" },
];

const STAGES = ["Group Stage", "Round of 32", "Round of 16", "Quarter-final", "Semi-final", "3rd Place", "Final"];

export default function Admin() {
  const [tab, setTab] = useState("matches");

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 pt-10 pb-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">Control Room · Admin Mode</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight uppercase leading-none">
            The <span className="bg-gradient-to-r from-amber-300 via-gold to-amber-500 bg-clip-text text-transparent">Admin</span> Bench
          </h1>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/5 pb-2">
          {[
            { k: "matches", l: "Matches", Icon: Calendar },
            { k: "knockout", l: "Knockout Setup", Icon: Sparkles },
            { k: "users", l: "Players", Icon: Users },
            { k: "bonus", l: "Bonus Results", Icon: Star },
            { k: "settings", l: "Settings", Icon: Settings },
          ].map(({ k, l, Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-colors inline-flex items-center gap-2 ${
                tab === k
                  ? "bg-gradient-to-r from-vivid to-purple text-white font-bold"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <Icon size={14}/> {l}
            </button>
          ))}
        </div>

        {tab === "matches" && <MatchesTab/>}
        {tab === "knockout" && <KnockoutTab/>}
        {tab === "users" && <UsersTab/>}
        {tab === "bonus" && <BonusTab/>}
        {tab === "settings" && <SettingsTab/>}
      </main>
    </div>
  );
}

function MatchesTab() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupFilter, setGroupFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [form, setForm] = useState({
    home_team: "", home_code: "br",
    away_team: "", away_code: "ar",
    date: "", time: "",
    stage: "Round of 32",
    group: "",
  });

  const load = () => {
    setLoading(true);
    api.get("/matches").then(({ data }) => setMatches(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    const c = COUNTRIES.find((c) => c.code === form.home_code);
    if (c) setForm((f) => ({ ...f, home_team: c.name }));
  }, [form.home_code]);
  useEffect(() => {
    const c = COUNTRIES.find((c) => c.code === form.away_code);
    if (c) setForm((f) => ({ ...f, away_team: c.name }));
  }, [form.away_code]);

  const createMatch = async (e) => {
    e.preventDefault();
    if (!form.home_team || !form.away_team || !form.date || !form.time) {
      toast.error("Fill all fields");
      return;
    }
    const kickoff = new Date(`${form.date}T${form.time}`).toISOString();
    try {
      await api.post("/matches", {
        home_team: form.home_team,
        home_code: form.home_code,
        away_team: form.away_team,
        away_code: form.away_code,
        kickoff,
        stage: form.stage,
        group: form.group || null,
      });
      toast.success("Match added");
      setForm({ ...form, date: "", time: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteMatch = async (id) => {
    if (!window.confirm("Delete this match and all its predictions?")) return;
    await api.delete(`/matches/${id}`);
    toast.success("Match deleted");
    load();
  };

  const setResult = async (id, h, a) => {
    try {
      await api.patch(`/matches/${id}/result`, { home_score: Number(h), away_score: Number(a) });
      toast.success("Result saved · points awarded · players notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const groups = ["all", ...Array.from(new Set(matches.map(m => m.group).filter(Boolean))).sort()];
  const stages = ["all", ...Array.from(new Set(matches.map(m => {
    if (m.group) return "Group Stage";
    return m.stage || "Other";
  })))];
  const filtered = matches.filter((m) => {
    const groupOk = groupFilter === "all" ? true : m.group === groupFilter;
    let stageOk = true;
    if (stageFilter !== "all") {
      const s = (m.stage || "").toLowerCase();
      const target = stageFilter.toLowerCase();
      if (stageFilter === "Group Stage") stageOk = !!m.group;
      else if (target === "final") stageOk = s === "final";
      else if (target === "semi-final") stageOk = s === "semi-final";
      else if (target === "quarter-final") stageOk = s === "quarter-final";
      else if (target === "round of 16") stageOk = s === "round of 16";
      else if (target === "round of 32") stageOk = s === "round of 32";
      else if (target === "3rd place") stageOk = s === "3rd place";
      else stageOk = s.includes(target);
    }
    return groupOk && stageOk;
  });

  return (
    <>
      {/* Result entry guide */}
      <div className="bg-surface border border-lavender/20 rounded-2xl p-4 mb-8 flex items-start gap-3" data-testid="admin-results-guide">
        <Info size={16} className="text-lavender shrink-0 mt-0.5"/>
        <div className="text-xs sm:text-sm text-white/70 leading-relaxed">
          <span className="text-lavender font-bold tracking-wider uppercase">How results work · </span>
          You only enter the <span className="text-lavender font-semibold">final 90-minute score</span>. The system automatically derives the
          <span className="text-lavender font-semibold"> winner</span> and <span className="text-lavender font-semibold">BTTS</span> outcomes and awards each player their points
          (+5 exact · +3 right winner · +1 BTTS, max 9/match). Players are notified instantly. Extra time / penalties are not counted.
        </div>
      </div>

      <form onSubmit={createMatch} className="bg-surface border border-white/5 rounded-2xl p-6 mb-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-5 flex items-center gap-2">
          <Plus size={12}/> Add New Match (for knockout rounds)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
          <TeamPicker label="Home Team" code={form.home_code} name={form.home_team}
            onCode={(v) => setForm({ ...form, home_code: v })}
            onName={(v) => setForm({ ...form, home_team: v })}
            testid="home"
          />
          <TeamPicker label="Away Team" code={form.away_code} name={form.away_team}
            onCode={(v) => setForm({ ...form, away_code: v })}
            onName={(v) => setForm({ ...form, away_team: v })}
            testid="away"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Date">
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="match-date" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple focus:ring-1 focus:ring-purple outline-none"/>
          </Field>
          <Field label="Kickoff Time">
            <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="match-time" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple focus:ring-1 focus:ring-purple outline-none"/>
          </Field>
          <Field label="Stage">
            <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="match-stage" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-gold focus:ring-1 focus:ring-gold outline-none">
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Group (optional)">
            <input type="text" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value.toUpperCase().slice(0,2) })} placeholder="A / B / leave blank" data-testid="match-group" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple focus:ring-1 focus:ring-purple outline-none"/>
          </Field>
        </div>
        <button type="submit" data-testid="create-match-btn" className="mt-6 bg-gradient-to-r from-vivid to-purple text-white font-bold uppercase tracking-wide rounded-full px-8 py-3 hover:from-purple hover:to-lavender hover:text-plum transition-all inline-flex items-center gap-2">
          <Plus size={16}/> Add Match
        </button>
      </form>

      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">All Matches ({matches.length} · showing {filtered.length})</div>
        </div>
        {stages.length > 2 && (
          <div className="flex flex-wrap gap-1.5">
            {stages.map((s) => (
              <button key={s} onClick={() => setStageFilter(s)} data-testid={`admin-stage-${s.replace(/\s+/g, "-").toLowerCase()}`} className={`px-3 py-1 rounded-full text-[10px] tracking-widest uppercase border ${stageFilter === s ? "bg-gradient-to-r from-vivid to-purple text-white border-purple font-bold" : "border-white/10 text-white/50 hover:border-purple"}`}>
                {s === "all" ? "All Stages" : s}
              </button>
            ))}
          </div>
        )}
        {groups.length > 2 && (
          <div className="flex flex-wrap gap-1.5">
            {groups.map((g) => (
              <button key={g} onClick={() => setGroupFilter(g)} data-testid={`admin-group-${g}`} className={`px-3 py-1 rounded-full text-[10px] tracking-widest uppercase border ${groupFilter === g ? "bg-lavender text-plum border-lavender font-bold" : "border-white/10 text-white/50 hover:border-purple"}`}>
                {g === "all" ? "All Groups" : `G${g}`}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? <div className="text-center text-white/40 py-10">Loading…</div>
        : filtered.length === 0 ? <div className="text-center text-white/40 py-10">No matches.</div>
        : <div className="flex flex-col gap-3">
            {filtered.map((m) => <AdminMatchRow key={m.id} m={m} onDelete={deleteMatch} onResult={setResult}/>)}
          </div>}
    </>
  );
}

function KnockoutTab() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [filter, setFilter] = useState("Round of 32");

  const load = () => {
    setLoading(true);
    api.get("/matches").then(({ data }) => {
      const knockout = data.filter(m => !m.group);
      setMatches(knockout);
      const map = {};
      knockout.forEach(m => { map[m.id] = { home: m.home_team, home_code: m.home_code, away: m.away_team, away_code: m.away_code }; });
      setDrafts(map);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const stages = ["Round of 32", "Round of 16", "Quarter-final", "Semi-final", "3rd Place", "Final"];
  const filtered = matches.filter(m => (m.stage || "").includes(filter));

  const updateDraft = (id, field, value) => {
    const c = COUNTRIES.find(c => c.code === value);
    setDrafts(d => ({
      ...d,
      [id]: {
        ...d[id],
        [field]: c ? c.name : d[id][field],
        [`${field}_code`]: value,
      }
    }));
  };

  const publish = async (id) => {
    const d = drafts[id];
    try {
      await api.put(`/matches/${id}`, {
        home_team: d.home,
        home_code: d.home_code,
        away_team: d.away,
        away_code: d.away_code,
      });
      toast.success("Match published");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="bg-surface border border-gold/40 rounded-2xl p-5 mb-6 shadow-[0_4px_28px_rgba(184,134,11,0.18)]">
        <div className="font-display text-xl uppercase tracking-wider flex items-center gap-2 mb-1 text-white"><Sparkles size={18} className="text-gold"/> Knockout Team Picker</div>
        <div className="text-sm text-white/75">Replace each placeholder (e.g. <span className="text-gold font-bold">2A</span>, <span className="text-gold font-bold">W73</span>) with the real qualified team. Save to publish — the match instantly appears with real flags on the Matches page.</div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {stages.map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`ko-stage-${s.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-3 py-1 rounded-full text-[10px] tracking-widest uppercase border ${filter === s ? "bg-gradient-to-r from-vivid to-purple text-white border-purple font-bold" : "border-white/10 text-white/50 hover:border-purple"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? <div className="text-center text-white/40 py-10">Loading…</div>
        : filtered.length === 0 ? <div className="text-center text-white/40 py-10">No matches in this stage.</div>
        : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map(m => {
              const d = drafts[m.id] || {};
              const placeholder = m.home_code === "un" || m.away_code === "un";
              return (
                <div key={m.id} className={`bg-surface border rounded-2xl p-4 ${placeholder ? "border-gold/30" : "border-lavender/30"}`} data-testid={`ko-row-${m.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] tracking-widest uppercase text-gold">{m.stage}</div>
                    <div className="text-[10px] tracking-widest uppercase text-white/40">{fmtUAE(m.kickoff)} {TZ_LABEL}</div>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center mb-3">
                    <select value={d.home_code || ""} onChange={(e) => updateDraft(m.id, "home", e.target.value)} data-testid={`ko-home-${m.id}`} className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:border-gold outline-none">
                      <option value="">— Home —</option>
                      {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                    <div className="font-display text-purple">VS</div>
                    <select value={d.away_code || ""} onChange={(e) => updateDraft(m.id, "away", e.target.value)} data-testid={`ko-away-${m.id}`} className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:border-gold outline-none">
                      <option value="">— Away —</option>
                      {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                  <button onClick={() => publish(m.id)} data-testid={`ko-publish-${m.id}`}
                    disabled={!d.home_code || !d.away_code || d.home_code === "un" || d.away_code === "un"}
                    className="w-full bg-gold text-plum font-bold uppercase tracking-wide text-xs rounded-full px-4 py-2 hover:bg-amber-300 transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2">
                    <Save size={12}/> {placeholder ? "Publish Match" : "Update Teams"}
                  </button>
                </div>
              );
            })}
          </div>}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleStatusChange = async (e, u, nextValue) => {
    e.stopPropagation();
    const willSuspend = nextValue === "suspended";
    if (willSuspend && !window.confirm(`Suspend @${u.league_name} (${u.name})? They will be unable to log in until reactivated.`)) return;
    setPendingId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/status`, { is_suspended: willSuspend });
      toast.success(`${u.name} ${willSuspend ? "suspended" : "reactivated"}`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_suspended: willSuspend } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status change failed");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Players ({users.length}) · Click a row to see full history</div>
      {loading ? <div className="text-center text-white/40 py-10">Loading…</div>
        : users.length === 0 ? <div className="text-center text-white/40 py-10">No players yet.</div>
        : <div className="bg-surface border border-white/5 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-4 px-5 py-3 text-[10px] tracking-widest uppercase text-white/40 border-b border-white/5">
              <div>Real Name</div>
              <div>League Name</div>
              <div>Role</div>
              <div className="text-right">Status</div>
            </div>
            {users.map((u) => {
              const status = u.is_suspended ? "suspended" : "active";
              return (
                <div
                  key={u.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(u.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") setSelectedId(u.id); }}
                  data-testid={`user-row-${u.id}`}
                  className={`w-full text-left grid grid-cols-[1fr_1fr_auto_auto] gap-4 px-5 py-3 border-b border-white/5 last:border-b-0 items-center hover:bg-gold/5 transition-colors cursor-pointer ${u.is_suspended ? "opacity-70" : ""}`}
                >
                  <div className="font-semibold truncate">{u.name}</div>
                  <div className="text-lavender truncate">@{u.league_name}</div>
                  <div>{u.is_admin
                    ? <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-gold/20 text-gold border border-gold/40">Admin</span>
                    : <span className="text-[10px] uppercase tracking-widest text-white/40">Player</span>
                  }</div>
                  <div className="text-right">
                    {u.is_admin ? (
                      <span className="text-[10px] uppercase tracking-widest text-white/30">—</span>
                    ) : (
                      <select
                        value={status}
                        onChange={(e) => handleStatusChange(e, u, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={pendingId === u.id}
                        data-testid={`user-status-${u.id}`}
                        className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border outline-none cursor-pointer transition-colors ${
                          u.is_suspended
                            ? "bg-red-500/20 text-red-200 border-red-500/50 hover:bg-red-500/30"
                            : "bg-green-500/15 text-green-300 border-green-500/40 hover:bg-green-500/25"
                        } ${pendingId === u.id ? "opacity-50" : ""}`}
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    )}
                    {!u.is_admin && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const flagged = !!u.rename_warning_at;
                          if (flagged) {
                            try {
                              await api.delete(`/admin/users/${u.id}/flag-rename`);
                              setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, rename_warning_at: null } : x)));
                              toast.success("Rename warning cleared");
                            } catch (err) { toast.error("Failed"); }
                          } else {
                            if (!window.confirm(`Send a sticky rename-warning popup to ${u.name}? They'll be asked to update their real name within 48h.`)) return;
                            try {
                              await api.patch(`/admin/users/${u.id}/flag-rename`);
                              setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, rename_warning_at: new Date().toISOString() } : x)));
                              toast.success(`Warning sent to ${u.name}`);
                            } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                          }
                        }}
                        data-testid={`flag-rename-${u.id}`}
                        title={u.rename_warning_at ? "Clear rename warning" : "Send 48h rename warning popup"}
                        className={`btn-bounce ml-2 inline-flex items-center justify-center w-9 h-9 rounded-full border text-sm transition-colors ${
                          u.rename_warning_at
                            ? "bg-red-500/20 text-red-200 border-red-500/60 hover:bg-red-500/30"
                            : "bg-white/5 text-white/40 border-white/15 hover:text-red-200 hover:border-red-500/50"
                        }`}
                      >
                        ⚠️
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
      }
      <PlayerDetailModal userId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function BonusTab() {
  const [results, setResults] = useState({});
  const [drafts, setDrafts] = useState({});
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [res, opts] = await Promise.all([
        api.get("/bonus/results"),
        api.get("/bonus/options"),
      ]);
      setResults(res.data || {});
      setDrafts(res.data || {});
      setOptions(opts.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const value = (drafts[key] || "").trim();
    try {
      const { data } = await api.post("/bonus/results", { type: key, value });
      toast.success(`Saved · ${data.awarded_count} players awarded points`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">Enter Final Tournament Outcomes</div>
      <div className="text-sm text-white/50 mb-6">Pick the actual result from the dropdown — players who match will earn the listed bonus points and get an in-app notification. <span className="text-lavender">All bonus outcomes are awarded based on official FIFA results.</span></div>
      {loading ? <div className="text-center text-white/40 py-10">Loading…</div>
        : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {BONUS_DEFS.map((b) => {
              const opts = options[b.key] || [];
              return (
              <div key={b.key} className="bg-surface border border-gold/20 rounded-2xl p-5" data-testid={`bonus-result-card-${b.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-xl uppercase tracking-wider">{b.label}</div>
                  <span className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full bg-gold/15 text-gold border border-gold/40">+{b.pts} pts</span>
                </div>
                <input
                  type="text"
                  list={`bonus-opts-${b.key}`}
                  value={drafts[b.key] || ""}
                  onChange={(e) => setDrafts({ ...drafts, [b.key]: e.target.value })}
                  placeholder="Pick a result or type for a tie: Egypt, Paraguay"
                  data-testid={`bonus-result-input-${b.key}`}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all"
                />
                <datalist id={`bonus-opts-${b.key}`}>
                  {opts.map((o) => (
                    <option key={o} value={b.kind === "group" ? `Group ${o}` : o} />
                  ))}
                </datalist>
                <div className="text-[10px] text-white/40 mt-1.5">💡 For ties, enter comma-separated: <span className="text-lavender">Egypt, Paraguay</span> — anyone who picked either wins.</div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-[10px] tracking-widest uppercase text-white/40">
                    {results[b.key] ? <>Current: <span className="text-lavender">{results[b.key]}</span></> : <span className="text-white/30">No result yet</span>}
                  </div>
                  <button
                    onClick={() => save(b.key)}
                    data-testid={`bonus-result-save-${b.key}`}
                    className="bg-gradient-to-r from-amber-400 to-yellow-500 text-plum font-bold uppercase tracking-widest text-[10px] rounded-full px-4 py-2 hover:from-yellow-300 hover:to-amber-400 transition-all inline-flex items-center gap-1.5"
                  >
                    <Save size={12}/> Save & Award
                  </button>
                </div>
              </div>
              );
            })}
          </div>}
    </div>
  );
}

function SettingsTab() {
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tournament/config");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  const toggleLock = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/tournament/config", { force_locked: next });
      setCfg(data);
      toast.success(next ? "Bonus predictions LOCKED" : "Bonus predictions OPEN");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryLock = async (key, next) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/tournament/config", {
        force_locked_categories: { [key]: next },
      });
      setCfg(data);
      const label = BONUS_DEFS.find((b) => b.key === key)?.label || key;
      toast.success(`${label} ${next ? "LOCKED" : "OPEN"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const isLocked = !!cfg.is_locked;
  const forcedByAdmin = !!cfg.force_locked;
  const autoLock = cfg.effective_lock_at && !forcedByAdmin;
  const categories = cfg.categories || {};

  return (
    <div className="bg-surface border border-white/5 rounded-2xl p-6 max-w-3xl">
      <div className="font-display text-2xl uppercase tracking-wider mb-2">Bonus Lock</div>
      <div className="text-sm text-white/60 mb-6 leading-relaxed">
        Bonus picks auto-lock <span className="text-lavender font-semibold">2 hours before the earliest Round of 32 kickoff</span>.
        Use the master toggle to lock everything at once, or the per-category toggles below for finer control.
      </div>

      {/* Master Toggle */}
      <div className="flex items-center justify-between gap-4 p-5 rounded-2xl bg-black/30 border border-white/10 mb-6">
        <div>
          <div className="font-display text-lg uppercase tracking-wider">Master Lock (all categories)</div>
          <div className="text-xs text-white/50 mt-1">{forcedByAdmin ? "All bonus predictions are locked by admin" : "Toggle ON to immediately stop ALL bonus prediction changes"}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={forcedByAdmin}
          onClick={() => toggleLock(!forcedByAdmin)}
          disabled={saving}
          data-testid="bonus-force-lock-toggle"
          className={`relative inline-flex items-center w-16 h-9 rounded-full transition-all duration-300 shrink-0 ${
            forcedByAdmin
              ? "bg-gradient-to-r from-vivid to-purple shadow-[0_0_18px_rgba(160,64,192,0.55)]"
              : "bg-white/10"
          } ${saving ? "opacity-60" : ""}`}
        >
          <span
            className={`inline-block w-7 h-7 bg-white rounded-full shadow-lg transform transition-transform duration-300 ${
              forcedByAdmin ? "translate-x-8" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Per-category toggles */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">Per-Category Lock</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {BONUS_DEFS.map((b) => {
          const c = categories[b.key] || {};
          const forced = !!c.force_locked;
          const locked = !!c.is_locked || forcedByAdmin;
          return (
            <div key={b.key} className="pop-hover flex items-center justify-between gap-3 p-4 rounded-xl bg-black/30 border border-white/10">
              <div className="min-w-0">
                <div className="font-display text-sm uppercase tracking-wider truncate">{b.label}</div>
                <div className={`text-[10px] tracking-widest uppercase mt-1 ${locked ? "text-lavender" : "text-gold"}`}>
                  {locked ? "🔒 Locked" : "🟢 Open"}{forcedByAdmin && !forced ? " (master)" : ""}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={forced}
                onClick={() => toggleCategoryLock(b.key, !forced)}
                disabled={saving || forcedByAdmin}
                data-testid={`category-lock-${b.key}`}
                title={forcedByAdmin ? "Disable Master Lock to use per-category" : ""}
                className={`relative inline-flex items-center w-12 h-7 rounded-full transition-all duration-300 shrink-0 ${
                  forced
                    ? "bg-gradient-to-r from-vivid to-purple shadow-[0_0_12px_rgba(160,64,192,0.45)]"
                    : "bg-white/10"
                } ${(saving || forcedByAdmin) ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${
                    forced ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      {/* Status Card */}
      <div className={`rounded-2xl p-5 border ${
        isLocked
          ? "bg-lavender/10 border-lavender/40"
          : "bg-gold/10 border-gold/40"
      }`} data-testid="bonus-lock-status">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Master Status</div>
          <span className={`text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full ${
            isLocked
              ? "bg-lavender/25 text-lavender border border-lavender/50"
              : "bg-gold/25 text-gold border border-gold/50"
          }`}>{isLocked ? "🔒 Locked" : "🟢 Open"}</span>
        </div>
        {autoLock ? (
          <div className="text-sm text-white/70">
            Auto-lock at <span className="text-lavender font-bold">{fmtUAE(cfg.effective_lock_at)} {TZ_LABEL}</span>
            <span className="block text-xs text-white/50 mt-1">(2h before earliest Round of 32 kickoff)</span>
          </div>
        ) : forcedByAdmin ? (
          <div className="text-sm text-white/70">Manually locked by admin. Players cannot create or edit bonus picks.</div>
        ) : (
          <div className="text-sm text-white/70">No Round of 32 fixtures scheduled — bonus picks remain open indefinitely.</div>
        )}
      </div>

      {/* Danger Zone — Wipe Test Data */}
      <div className="mt-10 rounded-2xl border-2 border-red-500/40 bg-red-500/5 p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-red-300 font-display text-lg uppercase tracking-wider">⚠️ Danger Zone</span>
        </div>
        <div className="text-sm text-white/70 mb-4 leading-relaxed">
          Wipe all <span className="text-red-300 font-bold">players, predictions, bonus picks, results, and notifications</span>.
          Keeps your admin account and all 104 fixtures. <span className="block mt-1 text-white/50 text-xs">Use this once before go-live to clear your test data.</span>
        </div>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm("Wipe ALL test data? This deletes every player, prediction, bonus pick, result, and notification. Cannot be undone.")) return;
            try {
              const { data } = await api.post("/admin/cleanup");
              toast.success(`Wiped: ${data.users_deleted} players · ${data.predictions_deleted} preds · ${data.bonus_predictions_deleted} bonus picks · ${data.notifications_deleted} notifications`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Cleanup failed");
            }
          }}
          data-testid="wipe-test-data-btn"
          className="btn-bounce inline-flex items-center gap-2 bg-red-700 hover:bg-red-600 text-white font-bold uppercase tracking-widest text-xs rounded-full px-5 py-2.5 border border-red-500 transition-colors"
        >
          🗑️ Wipe All Test Data
        </button>
      </div>
    </div>
  );
}

// ---------- Helpers ----------
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 block mb-2">{label}</label>
      {children}
    </div>
  );
}

function TeamPicker({ label, code, name, onCode, onName, testid }) {
  return (
    <div className="space-y-3">
      <label className="text-[10px] tracking-[0.3em] uppercase text-white/40 block">{label}</label>
      <div className="flex gap-3 items-center">
        <img src={flagUrl(code)} className="w-14 h-14 rounded-full ring-2 ring-white/10 object-cover" alt="" />
        <div className="flex-1 grid grid-cols-2 gap-2">
          <select value={code} onChange={(e) => onCode(e.target.value)} data-testid={`select-${testid}-country`} className="bg-black/40 border border-white/10 rounded-xl px-3 py-3 text-white focus:border-purple focus:ring-1 focus:ring-purple outline-none">
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <input value={name} onChange={(e) => onName(e.target.value)} placeholder="Team name" data-testid={`input-${testid}-name`} className="bg-black/40 border border-white/10 rounded-xl px-3 py-3 text-white focus:border-purple focus:ring-1 focus:ring-purple outline-none"/>
        </div>
      </div>
    </div>
  );
}

function AdminMatchRow({ m, onDelete, onResult }) {
  const [h, setH] = useState(m.home_score ?? "");
  const [a, setA] = useState(m.away_score ?? "");
  const [editing, setEditing] = useState(false);
  const [eh, setEh] = useState(m.home_team);
  const [ea, setEa] = useState(m.away_team);
  const [edate, setEdate] = useState(m.kickoff.slice(0, 10));
  const [etime, setEtime] = useState(() => {
    const d = new Date(m.kickoff);
    const uae = new Date(d.getTime() + 4 * 60 * 60 * 1000);
    return uae.toISOString().slice(11, 16);
  });
  useEffect(() => { setH(m.home_score ?? ""); setA(m.away_score ?? ""); }, [m.home_score, m.away_score]);

  const saveEdit = async () => {
    try {
      // UAE → UTC: subtract 4h
      const [hh, mm] = etime.split(":").map(Number);
      const uae = new Date(`${edate}T${etime}:00+04:00`);
      void hh; void mm;
      await api.put(`/matches/${m.id}`, {
        home_team: eh.trim(),
        away_team: ea.trim(),
        kickoff: uae.toISOString(),
      });
      toast.success("Match updated");
      setEditing(false);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const fmt = `${fmtUAE(m.kickoff)} ${TZ_LABEL}`;

  // Derived preview of what the score implies
  const hN = h === "" ? null : Number(h);
  const aN = a === "" ? null : Number(a);
  let preview = null;
  if (hN !== null && aN !== null) {
    const winner = hN > aN ? `${m.home_team} win` : aN > hN ? `${m.away_team} win` : "Draw";
    const btts = hN > 0 && aN > 0 ? "BTTS: Yes" : "BTTS: No";
    preview = `→ ${winner} · ${btts}`;
  }

  return (
    <div className="bg-surface border border-white/5 rounded-2xl p-5 grid grid-cols-1 lg:grid-cols-[1fr_auto_auto] gap-4 items-center">
      <div className="flex items-center gap-3 min-w-0 flex-wrap">
        {m.group && <span className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full bg-vivid/15 text-lavender border border-vivid/40">G{m.group}</span>}
        <img src={flagUrl(m.home_code)} className="w-10 h-10 rounded-full ring-1 ring-white/10 object-cover" alt="" />
        <div className="truncate font-semibold">{m.home_team}</div>
        <div className="text-white/30 font-display">VS</div>
        <div className="truncate font-semibold">{m.away_team}</div>
        <img src={flagUrl(m.away_code)} className="w-10 h-10 rounded-full ring-1 ring-white/10 object-cover" alt="" />
        <div className="hidden md:block text-[10px] tracking-widest uppercase text-white/40">{m.stage} · {fmt}</div>
      </div>

      <div className="flex items-center gap-2 justify-center flex-col md:flex-row">
        <div className="flex items-center gap-2">
          <input type="number" min="0" value={h} onChange={(e) => setH(e.target.value)} data-testid={`result-home-${m.id}`} className="w-14 h-12 text-center font-mono font-bold text-xl bg-black/40 border border-white/15 rounded-lg focus:border-purple focus:ring-1 focus:ring-purple outline-none"/>
          <span className="text-white/30 font-display text-xl">:</span>
          <input type="number" min="0" value={a} onChange={(e) => setA(e.target.value)} data-testid={`result-away-${m.id}`} className="w-14 h-12 text-center font-mono font-bold text-xl bg-black/40 border border-white/15 rounded-lg focus:border-purple focus:ring-1 focus:ring-purple outline-none"/>
          <button onClick={() => onResult(m.id, h, a)} data-testid={`save-result-${m.id}`} disabled={h === "" || a === ""}
            className="ml-1 px-4 py-3 rounded-full bg-gradient-to-r from-vivid to-purple text-white font-bold uppercase text-xs tracking-widest hover:from-purple hover:to-lavender hover:text-plum transition-all disabled:opacity-40 inline-flex items-center gap-2">
            <Save size={14}/> Save
          </button>
        </div>
        {preview && (
          <div className="text-[10px] tracking-widest uppercase text-lavender" data-testid={`result-preview-${m.id}`}>
            {preview}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 justify-end">
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
          m.status === "finished" ? "bg-lavender/15 text-lavender border border-lavender/40" : "bg-purple/15 text-purple border border-purple/40"
        }`}>{m.status}</span>
        <button onClick={() => setEditing(!editing)} data-testid={`edit-match-${m.id}`} className="w-10 h-10 rounded-full border border-white/10 text-white/60 hover:border-gold hover:text-gold grid place-items-center transition-colors" title="Edit match">
          <Pencil size={16}/>
        </button>
        <button onClick={() => onDelete(m.id)} data-testid={`delete-match-${m.id}`} className="w-10 h-10 rounded-full border border-white/10 text-white/60 hover:border-red-400 hover:text-red-400 grid place-items-center transition-colors" title="Delete">
          <Trash2 size={16}/>
        </button>
      </div>
      {editing && (
        <div className="col-span-full mt-2 pt-3 border-t border-white/5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid={`edit-form-${m.id}`}>
          <Field label="Home team">
            <input value={eh} onChange={(e) => setEh(e.target.value)} data-testid={`edit-home-${m.id}`} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white focus:border-gold outline-none"/>
          </Field>
          <Field label="Away team">
            <input value={ea} onChange={(e) => setEa(e.target.value)} data-testid={`edit-away-${m.id}`} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white focus:border-gold outline-none"/>
          </Field>
          <Field label="Date">
            <input type="date" value={edate} onChange={(e) => setEdate(e.target.value)} data-testid={`edit-date-${m.id}`} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white focus:border-gold outline-none"/>
          </Field>
          <div className="flex gap-2 items-end">
            <Field label="Kickoff (UAE)">
              <input type="time" value={etime} onChange={(e) => setEtime(e.target.value)} data-testid={`edit-time-${m.id}`} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white focus:border-gold outline-none"/>
            </Field>
            <button onClick={saveEdit} data-testid={`save-edit-${m.id}`} className="px-4 py-2 rounded-full bg-gold text-plum font-bold uppercase text-xs tracking-widest hover:bg-amber-300 transition-all inline-flex items-center gap-1.5">
              <Save size={12}/> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
